import { GiftFilled, VoucherFilled } from '@teko/ds-icon';
import { MoneyPromotion, ProductPromotion } from '@teko/ecommerce';
import { Currency, styled, ThemeProps } from '@teko/ui';
import { ThemeProps as DsThemeProps } from '@teko/ds-desktop';
import { t } from 'i18n';
import React from 'react';

interface PromotionOptionProps {
  promotion: ProductPromotion;
  className?: string;
  active?: boolean;
  onClick: (id: number) => void;
  productUnitName?: string;
}

const getTextDiscount = (
  moneyPromotion: MoneyPromotion,
  blockSizeDecimal: number,
  productUnitName: string,
) => {
  if (moneyPromotion.discountType === 'percent') {
    // Giảm %
    return (
      <>
        {t('Giảm')} {moneyPromotion.percent}%
        {!!moneyPromotion.maxDiscount && (
          <>
            {' '}
            {t('tối đa')} <Currency value={moneyPromotion.maxDiscount || 0} />
          </>
        )}
      </>
    );
  }

  const blockSizeText = blockSizeDecimal > 1 ? blockSizeDecimal : '';
  const productUnitNameText = productUnitName || t('sản phẩm');
  return (
    <>
      {t('Giảm')} <Currency value={moneyPromotion.money || 0} />
      {` / ${blockSizeText}`}
      {` ${productUnitNameText}`}
    </>
  );
};

export const renderIcon = (promo: ProductPromotion) => {
  const { benefit = {} } = promo || {};

  const isGiftOrMoneyBenefit = benefit.money?.length || benefit.items?.length;
  return isGiftOrMoneyBenefit ? (
    <GiftFilled color="primary500" size={32} />
  ) : (
    <VoucherFilled color="primary500" size={32} />
  );
};

export const getPromoTitle = (
  promo: ProductPromotion,
  blockSizeDecimal: number,
  productUnitName: string,
) => {
  const { benefit = {} } = promo || {};
  const titles = [];

  // Example: Giảm 10.000đ / Kg, tặng 2x quà, 1x voucher

  // Money
  if (benefit.money?.length) {
    titles.push(
      getTextDiscount(benefit.money[0], blockSizeDecimal, productUnitName),
    );
  }

  let prefixForGiftVoucher = `${
    titles.length ? t('Tặng').toLowerCase() : t('Tặng')
  } `;

  // Gift
  if (benefit.items?.length) {
    let itemTitle = prefixForGiftVoucher;
    prefixForGiftVoucher = '';

    benefit.items.forEach(({ quantity, name }) => {
      itemTitle += `${quantity}x ${name}, `;
    });
    titles.push(itemTitle.substr(0, itemTitle.length - 2));
  }

  // Voucher
  if (benefit.voucher?.name) {
    const { voucher } = benefit;

    titles.push(`${prefixForGiftVoucher}${voucher.quantity}x ${voucher.name}`);
  }

  const totalBenefits = titles.length;
  return (
    <>
      {titles.map((title, index) => {
        const isLastTitle = index === totalBenefits - 1;

        return (
          <React.Fragment key={index}>
            {title}
            {!isLastTitle && ', '}
          </React.Fragment>
        );
      })}
    </>
  );
};

export const getConditionText = (
  minQuantity: number,
  productUnitName: string,
): string | null => {
  if (!minQuantity) return null;

  return t('Khi mua từ {{quantity}} {{productUnitName}} trở lên', {
    quantity: minQuantity,
    productUnitName: productUnitName || t('sản phẩm'),
  });
};

export const PromotionOption: React.FC<PromotionOptionProps> = ({
  promotion,
  className,
  active = false,
  onClick,
  productUnitName = '',
  ...props
}: PromotionOptionProps) => {
  const renderPromotionContent = () => {
    const blockSizeDecimal = promotion?.condition?.blockSizeDecimal || 0;
    const minQuantityDecimal = promotion?.condition?.minQuantityDecimal || 0;
    const conditionText = getConditionText(minQuantityDecimal, productUnitName);

    return (
      <PromotionContent>
        <div>
          <Label active={active}>
            {getPromoTitle(promotion, blockSizeDecimal, productUnitName)}
          </Label>
          {conditionText && <ConditionText>{conditionText}</ConditionText>}
        </div>
        <ExpiredAndApplied>
          {promotion.endedAt ? (
            <ExpiredAt>{`${t('HSD')}: ${new Date(
              promotion.endedAt,
            ).toLocaleDateString('vi')}`}</ExpiredAt>
          ) : null}
          <CTA>{active ? t('Bỏ chọn') : t('Áp dụng')}</CTA>
        </ExpiredAndApplied>
      </PromotionContent>
    );
  };

  return (
    <Item
      className={className}
      isActive={active}
      onClick={() => onClick(promotion.id)}
      {...props}
    >
      <IconWrapper>{renderIcon(promotion)}</IconWrapper>
      {renderPromotionContent()}
    </Item>
  );
};
const Item = styled.div(
  ({
    isActive,
    theme,
  }: DsThemeProps & {
    isActive: boolean;
  }) => `
    width: 100%;
    display: flex;
    margin-top: 10px;
    padding: 12px;
    border-radius: 8px;
  
    background-color: ${isActive ? theme?.colors.primary50 : '#ffffff'};
    border: 1px solid ${isActive ? theme?.colors.primary500 : '#E4E5F0'};
    cursor: pointer;
  `,
);

const IconWrapper = styled.div(
  ({ theme }: DsThemeProps) => `
  height: 64px;
  width: 64px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 12px;
  border-radius: 4px;
  background-color: ${theme?.colors.primary100};
`,
);

const PromotionContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const maxLineCss = (maxLines: number) => `
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: ${maxLines};
  `;

const Label = styled.div(
  ({ active, theme }: ThemeProps & { active: boolean }) => `
      font-size: 13px;
      font-weight: 500;
      line-height: 20px;
      ${maxLineCss(2)}
      color: ${active ? '#1B1D29' : theme?.colors.text};
    `,
);

const ConditionText = styled.div(
  ({ theme }: ThemeProps) => `
    margin-top: 4px;
    font-size: 12px;
    line-height: 16px;
    color: ${theme?.colors.neutral};
    ${maxLineCss(2)}
  `,
);

const ExpiredAndApplied = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
`;

const ExpiredAt = styled.div(
  ({ theme }: ThemeProps) =>
    `
      font-size: 12px;
      line-height: 16px;
      color: ${theme?.colors.neutral};
    `,
);

const CTA = styled.div(
  ({ theme }: ThemeProps) =>
    `
      font-size: 13px;
      line-height: 20px;
      color: ${theme?.colors.link500};
    `,
);
